import { useState } from "react";
import { Link } from "@/lib/router-compat";
import { useRoOnlyGuard } from '@/hooks/useRoOnlyGuard';
import { usePlatformStats } from '@/hooks/usePlatformStats';

const NAVY = "#0A1F1C";      // Petrol
const BLUE = "#C9A46B";      // Bronze/Gold accent
const GREEN = "#5B8C7B";     // Muted petrol green
const CREAM = "#F4EFE6";


const CheckIcon = ({ color = GREEN }: { color?: string }) => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <circle cx="9" cy="9" r="9" fill={color} opacity="0.12" />
    <path d="M5 9L8 12L13 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Arrow = ({ color = "white" }: { color?: string }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const STEPS = [
  { num: "01", title: "Completezi formularul", desc: "Durează 2 minute. Ne dai datele dealership-ului tău." },
  { num: "02", title: "Verificăm profilul tău", desc: "Echipa noastră verifică dealership-ul în 24 de ore." },
  { num: "03", title: "Primești badge-ul", desc: "Îți trimitem badge-ul gata de pus pe site-ul tău." },
  { num: "04", title: "Apar anunțurile tale", desc: "Mașinile tale ajung la cumpărători din toată România." },
];

const BENEFITS = [
  { color: NAVY, title: "Badge de Dealer Verificat", desc: "Afișezi pe site-ul tău că ești partener verificat. Cumpărătorii au încredere mai mare." },
  { color: BLUE, title: "Profil dedicat pe platformă", desc: "Pagina ta de dealer cu toate anunțurile, recenziile și datele de contact." },
  { color: GREEN, title: "Vizibilitate crescută", desc: "Anunțurile tale apar primele în căutările relevante pentru zona ta." },
  { color: "#7C3AED", title: "Statistici și rapoarte", desc: "Vezi câte persoane au văzut anunțurile, câți au dat click și câți te-au contactat." },
  { color: "#DC2626", title: "Acoperire națională", desc: "Ajungi la cumpărători din toată România, nu doar din orașul tău." },
  { color: "#D97706", title: "Anunțuri nelimitate", desc: "Publici tot stocul tău fără limită și fără costuri suplimentare." },
];

const PLANS = [
  {
    name: "Dealer Verificat",
    price: "de la €29,99",
    period: "/ lună",
    highlight: true,
    features: [
      "3 pachete: Start (5 anunțuri), Standard (60 anunțuri), Mare (nelimitat)",
      "Badge Dealer Verificat",
      "Profil complet cu logo și date contact",
      "Prioritate în căutări",
      "Statistici și rapoarte lunare",
      "Suport prioritar",
      "Acoperire națională",
    ],
    cta: "Începe acum",
  },
];

const FIELDS = [
  { id: "name", label: "Numele dealership-ului", placeholder: "ex: Auto Ionescu SRL" },
  { id: "contact", label: "Persoana de contact", placeholder: "Numele și prenumele" },
  { id: "phone", label: "Telefon", placeholder: "07XX XXX XXX" },
  { id: "email", label: "Email", placeholder: "office@dealer.ro" },
  { id: "city", label: "Orașul", placeholder: "ex: București" },
];

export default function Partners() {
  const __roBlocked = useRoOnlyGuard();
  const { data: stats } = usePlatformStats();
  const [activeSection, setActiveSection] = useState("beneficii");
  const [formData, setFormData] = useState<Record<string, string>>({});
  
  const [agreed, setAgreed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, boolean>>({});

  const scrollTo = (id: string) => {
    setActiveSection(id);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const handleSubmit = () => {
    const newErrors: Record<string, boolean> = {};
    FIELDS.forEach((f) => { if (!formData[f.id]) newErrors[f.id] = true; });
    
    if (!agreed) newErrors.agreed = true;
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
    setSubmitted(true);
  };

  const TABS = [
    { id: "beneficii", label: "Beneficii" },
    { id: "cum-functioneaza", label: "Cum funcționează" },
    { id: "planuri", label: "Planuri" },
    { id: "formular", label: "Înregistrare" },
  ];

  return (
    <div style={{ fontFamily: "'Work Sans', Inter, system-ui, sans-serif", background: CREAM, minHeight: "100vh" }}>
      {/* HERO */}
      <div style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #142F2A 100%)`, padding: "48px 20px 56px", position: "relative", overflow: "hidden" }}>

        <div style={{ position: "absolute", top: -40, right: -40, width: 200, height: 200, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
        <div style={{ position: "absolute", bottom: -60, left: -30, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.03)" }} />
        <div style={{ maxWidth: 480, margin: "0 auto", position: "relative" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.12)", borderRadius: 20, padding: "6px 14px", marginBottom: 20 }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#4ADE80" }} />
            <span style={{ color: "white", fontSize: 12, fontWeight: 600 }}>Înregistrări deschise</span>
          </div>
          <h1 style={{ fontFamily: "'Instrument Serif', Georgia, serif", color: CREAM, fontSize: 34, fontWeight: 400, lineHeight: 1.15, margin: "0 0 14px", letterSpacing: "-0.01em" }}>
            Vinde mai multe mașini cu RO Auto Market
          </h1>

          <p style={{ color: "rgba(255,255,255,0.75)", fontSize: 15, lineHeight: 1.6, margin: "0 0 28px" }}>
            Alătură-te dealerilor verificați din România. Badge de partener, profil dedicat și vizibilitate crescută pentru stocul tău.
          </p>
          <div style={{ display: "flex", gap: 24, marginBottom: 32 }}>
            {(stats ? [{ val: stats.activeListings.toLocaleString('ro-RO'), label: "Anunțuri active" }, { val: stats.verifiedDealers.toLocaleString('ro-RO'), label: "Dealeri verificați" }] : []).map((s) => (
              <div key={s.label} style={{ textAlign: "center" }}>
                <div style={{ color: "white", fontSize: 20, fontWeight: 800 }}>{s.val}</div>
                <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 10, marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
          <button onClick={() => scrollTo("formular")} style={{ background: "white", color: NAVY, border: "none", borderRadius: 12, padding: "14px 28px", fontSize: 15, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, boxShadow: "0 4px 20px rgba(0,0,0,0.2)" }}>
            Vreau să devin partener <Arrow color={NAVY} />
          </button>
        </div>
      </div>

      {/* BADGE SHOWCASE */}
      <div style={{ background: "white", padding: "32px 20px", borderBottom: "1px solid #F1F5F9" }}>
        <div style={{ maxWidth: 480, margin: "0 auto" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: BLUE, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>Ce primești</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#0F172A", marginBottom: 4 }}>Badge-ul tău de dealer verificat</div>
          <div style={{ fontSize: 12, color: "#64748B", marginBottom: 24, lineHeight: 1.5 }}>Îl pui pe site-ul tău și cumpărătorii știu instant că ești verificat pe RO Auto Market.</div>

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Seal */}
            <div style={{ background: "#F8FAFC", borderRadius: 16, padding: "24px", display: "flex", alignItems: "center", gap: 20, border: "1px solid #F1F5F9" }}>
              <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: 100, height: 100, borderRadius: "50%", background: "conic-gradient(from 0deg, #1B2E6B, #0F1F4D, #1B2E6B)", boxShadow: "0 6px 24px #1B2E6B77", position: "relative", flexShrink: 0 }}>
                <div style={{ position: "absolute", inset: 5, borderRadius: "50%", border: "1.5px solid #F5C84255" }} />
                <div style={{ position: "absolute", inset: 12, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.15)" }} />
                {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
                  <div key={deg} style={{ position: "absolute", width: 3, height: 3, borderRadius: "50%", background: "#F5C842", opacity: 0.7, top: "50%", left: "50%", transform: `rotate(${deg}deg) translateY(-43px) translate(-50%,-50%)` }} />
                ))}
                <div style={{ position: "relative", textAlign: "center", zIndex: 1 }}>
                  <svg width="28" height="28" viewBox="0 0 36 36" fill="none">
                    <path d="M18 3L21.5 10.5L30 11.5L24 17.5L25.5 26L18 22L10.5 26L12 17.5L6 11.5L14.5 10.5L18 3Z" fill="#F5C842" stroke="#E5A800" strokeWidth="0.8" />
                    <circle cx="18" cy="17" r="5.5" fill="none" stroke="white" strokeWidth="1.5" />
                    <path d="M14.5 17L17 19.5L21.5 14.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <div style={{ fontSize: 7, fontWeight: 800, color: "white", letterSpacing: 1, textTransform: "uppercase", marginTop: 4, lineHeight: 1.3 }}>
                    <div>DEALER</div><div>VERIFICAT</div>
                  </div>
                </div>
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#0F172A", marginBottom: 4 }}>Sigiliu oficial</div>
                <div style={{ fontSize: 11, color: "#64748B", lineHeight: 1.5 }}>Ideal pentru homepage sau secțiunea Hero a site-ului tău.</div>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#FEF9C3", borderRadius: 6, padding: "3px 8px", marginTop: 8 }}>
                  <div style={{ width: 4, height: 4, borderRadius: "50%", background: "#D97706" }} />
                  <span style={{ fontSize: 10, fontWeight: 700, color: "#92400E" }}>Recomandat</span>
                </div>
              </div>
            </div>

            {/* Horizontal */}
            <div style={{ background: "#F8FAFC", borderRadius: 16, padding: "24px", border: "1px solid #F1F5F9" }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 0, borderRadius: 12, overflow: "hidden", boxShadow: "0 4px 16px #1B2E6B44", height: 58, marginBottom: 12 }}>
                <div style={{ background: "linear-gradient(160deg, #1B2E6B, #0F1F4D)", width: 58, height: "100%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, position: "relative" }}>
                  <div style={{ position: "absolute", top: 3, left: 3, width: 8, height: 8, borderRadius: "50%", background: "#F5C842", opacity: 0.5 }} />
                  <svg width="28" height="28" viewBox="0 0 36 36" fill="none">
                    <path d="M18 3L21.5 10.5L30 11.5L24 17.5L25.5 26L18 22L10.5 26L12 17.5L6 11.5L14.5 10.5L18 3Z" fill="#F5C842" stroke="#E5A800" strokeWidth="0.8" />
                    <path d="M14.5 17L17 19.5L21.5 14.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div style={{ background: "white", flex: 1, height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", padding: "0 14px", borderTop: "3px solid #F5C842" }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: "#0F172A" }}>Dealer Verificat</div>
                  <div style={{ fontSize: 9, fontWeight: 600, color: "#1B2E6B", letterSpacing: 1.5, textTransform: "uppercase", marginTop: 2 }}>RO AUTO MARKET</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 3, marginTop: 3 }}>
                    <div style={{ width: 4, height: 4, borderRadius: "50%", background: "#22C55E" }} />
                    <div style={{ fontSize: 8, color: "#64748B" }}>Verificat oficial</div>
                  </div>
                </div>
              </div>
              <div style={{ fontSize: 11, color: "#64748B", lineHeight: 1.5 }}>Ideal pentru footer sau sidebar. Se integrează discret pe orice site.</div>
            </div>

            {/* Compact pill */}
            <div style={{ background: "#F8FAFC", borderRadius: 16, padding: "24px", border: "1px solid #F1F5F9" }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 12, background: "linear-gradient(135deg, #1B2E6B 0%, #0F1F4D 100%)", borderRadius: 50, padding: "10px 22px 10px 10px", boxShadow: "0 4px 20px #1B2E6B66", marginBottom: 12, position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "50%", borderRadius: "50px 50px 0 0", background: "rgba(255,255,255,0.05)" }} />
                <div style={{ width: 38, height: 38, borderRadius: "50%", background: "rgba(255,255,255,0.12)", display: "flex", alignItems: "center", justifyContent: "center", border: "1.5px solid #F5C84266", flexShrink: 0 }}>
                  <svg width="22" height="22" viewBox="0 0 36 36" fill="none">
                    <path d="M18 3L21.5 10.5L30 11.5L24 17.5L25.5 26L18 22L10.5 26L12 17.5L6 11.5L14.5 10.5L18 3Z" fill="#F5C842" stroke="#E5A800" strokeWidth="0.8" />
                    <path d="M14.5 17L17 19.5L21.5 14.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "white", letterSpacing: 0.3 }}>Dealer Verificat</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 3 }}>
                    <div style={{ width: 4, height: 4, borderRadius: "50%", background: "#F5C842" }} />
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#FDE68A", letterSpacing: 1.8, textTransform: "uppercase" }}>RO AUTO MARKET</div>
                  </div>
                </div>
              </div>
              <div style={{ fontSize: 11, color: "#64748B", lineHeight: 1.5 }}>Ideal pentru header sau versiunea mobilă a site-ului tău.</div>
            </div>
          </div>

          <Link to="/parteneri/badge" style={{ width: "100%", marginTop: 20, padding: "14px", background: NAVY, color: "white", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, textDecoration: "none" }}>
            Descarcă badge-ul (PNG) <Arrow />
          </Link>
        </div>
      </div>


      {/* STICKY TABS */}
      <div style={{ background: "white", borderBottom: "1px solid #E2E8F0", position: "sticky", top: 0, zIndex: 10, overflowX: "auto" }}>
        <div style={{ maxWidth: 480, margin: "0 auto", display: "flex" }}>
          {TABS.map((tab) => (
            <button key={tab.id} onClick={() => scrollTo(tab.id)} style={{ padding: "14px 16px", border: "none", background: "transparent", fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", color: activeSection === tab.id ? NAVY : "#94A3B8", borderBottom: activeSection === tab.id ? `2px solid ${NAVY}` : "2px solid transparent" }}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 480, margin: "0 auto", padding: "0 16px" }}>
        {/* BENEFICII */}
        <div id="beneficii" style={{ paddingTop: 32, paddingBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: BLUE, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>De ce să alegi</div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: "#1E293B", margin: "0 0 20px" }}>Ce primești ca partener</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {BENEFITS.map((b, i) => (
              <div key={i} style={{ display: "flex", gap: 14, alignItems: "flex-start", background: "white", borderRadius: 12, padding: "16px", border: "1px solid #F1F5F9", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
                <div style={{ width: 40, height: 40, borderRadius: "50%", background: b.color + "14", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <div style={{ width: 16, height: 16, borderRadius: 3, background: b.color }} />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#1E293B", marginBottom: 4 }}>{b.title}</div>
                  <div style={{ fontSize: 12, color: "#64748B", lineHeight: 1.5 }}>{b.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CUM FUNCTIONEAZA */}
        <div id="cum-functioneaza" style={{ paddingTop: 36, paddingBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: BLUE, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>Procesul</div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: "#1E293B", margin: "0 0 20px" }}>Cum funcționează</h2>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {STEPS.map((s, i) => (
              <div key={i} style={{ display: "flex", gap: 16 }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <div style={{ width: 44, height: 44, borderRadius: "50%", background: NAVY, color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, flexShrink: 0 }}>{s.num}</div>
                  {i < STEPS.length - 1 && <div style={{ width: 2, flex: 1, background: "#E2E8F0", margin: "4px 0" }} />}
                </div>
                <div style={{ paddingBottom: i < STEPS.length - 1 ? 24 : 0, paddingTop: 10 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#1E293B", marginBottom: 4 }}>{s.title}</div>
                  <div style={{ fontSize: 12, color: "#64748B", lineHeight: 1.5 }}>{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* PLANURI */}
        <div id="planuri" style={{ paddingTop: 36, paddingBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: BLUE, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>Prețuri</div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: "#1E293B", margin: "0 0 20px" }}>Alege planul potrivit</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {PLANS.map((p, i) => (
              <div key={i} style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #142F2A 100%)`, borderRadius: 20, padding: "24px", boxShadow: "0 12px 40px rgba(10,31,28,0.35)", position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
                <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#F5C842", borderRadius: 20, padding: "5px 12px", marginBottom: 16 }}>
                  <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#92400E" }} />
                  <span style={{ fontSize: 10, fontWeight: 800, color: "#78350F", letterSpacing: 0.5 }}>3 PACHETE DISPONIBILE</span>
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "rgba(255,255,255,0.6)", marginBottom: 8, letterSpacing: 0.5 }}>{p.name}</div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginBottom: 6 }}>
                  <span style={{ fontSize: 36, fontWeight: 800, color: "white" }}>{p.price}</span>
                  <span style={{ fontSize: 14, color: "rgba(255,255,255,0.5)" }}>{p.period}</span>
                </div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", marginBottom: 20 }}>Start 29,99€ · Standard 50€ · Mare 120€</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
                  {p.features.map((f, j) => (
                    <div key={j} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <CheckIcon color={p.highlight ? "#4ADE80" : GREEN} />
                      <span style={{ fontSize: 13, color: p.highlight ? "rgba(255,255,255,0.85)" : "#475569" }}>{f}</span>
                    </div>
                  ))}
                </div>
                <button onClick={() => scrollTo("formular")} style={{ width: "100%", padding: "16px", background: "#F5C842", color: "#0F172A", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, letterSpacing: 0.3 }}>
                  {p.cta} <Arrow color="#0F172A" />
                </button>
                <div style={{ textAlign: "center", marginTop: 10, fontSize: 11, color: "rgba(255,255,255,0.4)" }}>
                  Anulare oricând, fără penalizări
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* FORMULAR */}
        <div id="formular" style={{ paddingTop: 36, paddingBottom: 56 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: BLUE, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>Înregistrare</div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: "#1E293B", margin: "0 0 6px" }}>Trimite cererea ta</h2>
          <p style={{ fontSize: 13, color: "#64748B", margin: "0 0 20px", lineHeight: 1.5 }}>Completează formularul și te contactăm în maxim 24 de ore.</p>

          {submitted ? (
            <div style={{ background: "#F0FDF4", border: "1px solid #86EFAC", borderRadius: 16, padding: "40px 20px", textAlign: "center" }}>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: GREEN, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M5 12L10 17L19 8" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </div>
              <div style={{ fontSize: 17, fontWeight: 800, color: "#15803D", marginBottom: 8 }}>Cererea a fost trimisă!</div>
              <div style={{ fontSize: 13, color: "#166534", lineHeight: 1.5 }}>Te contactăm în maxim 24 de ore la datele furnizate.</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {FIELDS.map((f) => (
                <div key={f.id}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 6 }}>{f.label}</div>
                  <input
                    placeholder={f.placeholder}
                    value={formData[f.id] || ""}
                    onChange={(e) => { setFormData((p) => ({ ...p, [f.id]: e.target.value })); setErrors((p) => ({ ...p, [f.id]: false })); }}
                    style={{ width: "100%", padding: "13px 14px", border: `1.5px solid ${errors[f.id] ? "#EF4444" : "#E2E8F0"}`, borderRadius: 10, fontSize: 14, color: "#1E293B", background: "white", outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
                  />
                  {errors[f.id] && <div style={{ fontSize: 11, color: "#EF4444", marginTop: 4 }}>Câmp obligatoriu</div>}
                </div>
              ))}


              <div onClick={() => { setAgreed(!agreed); setErrors((p) => ({ ...p, agreed: false })); }} style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer", marginTop: 4 }}>
                <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${errors.agreed ? "#EF4444" : agreed ? NAVY : "#CBD5E1"}`, background: agreed ? NAVY : "white", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                  {agreed && <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5L4 7L8 3" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                </div>
                <div style={{ fontSize: 12, color: "#64748B", lineHeight: 1.5 }}>
                  Sunt de acord cu termenii și condițiile RO Auto Market și cu prelucrarea datelor mele.
                </div>
              </div>
              {errors.agreed && <div style={{ fontSize: 11, color: "#EF4444", marginTop: -8 }}>Bifează acordul pentru a continua</div>}

              <button onClick={handleSubmit} style={{ width: "100%", padding: "16px", background: NAVY, color: "white", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, boxShadow: "0 4px 16px rgba(27,46,107,0.3)", marginTop: 4 }}>
                Trimite cererea <Arrow />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
